
import { useMemo } from 'react';
import { useTransactionsXp } from '../../hooks/fetch_api_hook/useTransactionsXp';
import { useXp } from '../../context/XpContext';

/**
 * Mini-feed des derniers gains d'XP (GET /transactions-xp/, antéchronologique).
 * Shape vérifiée côté back (TransactionXPSerializer) :
 *   { id, montant, source: 'quiz_session'|'eval_nationale'|'palier_serie'|'quiz_auto',
 *     matiere, ref_id, metadata: { nb_bonnes, nb_mauvaises } | { palier }, created_at }
 * — normalisation défensive quand même (champs alternatifs tolérés).
 * Re-fetch automatique : le hook est keyé sur profil.xp_total (rafraîchi après
 * chaque soumission par XpContext).
 */

const LIBELLES_SOURCE = {
    quiz_session: 'Quiz',
    eval_nationale: 'Évaluation',
    palier_serie: 'Palier de série 🔥',
    quiz_auto: 'Exercices',
};

const normalize = (t) => ({
    id: t.id ?? `${t.source ?? 'tx'}-${t.created_at ?? Math.random()}`,
    xp: t.montant ?? t.xp ?? t.delta ?? 0,
    date: t.created_at ?? t.date ?? t.horodatage ?? null,
    source: t.source ?? '',
    matiere: t.matiere ?? '',
    metadata: t.metadata ?? {},
});

const rtf = typeof Intl !== 'undefined' && Intl.RelativeTimeFormat
    ? new Intl.RelativeTimeFormat('fr', { numeric: 'auto' })
    : null;

const dateRelative = (iso) => {
    if (!iso || !rtf) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const jours = Math.round((d.getTime() - Date.now()) / 86400000);
    if (jours > -30) return rtf.format(jours, 'day');       // « aujourd'hui », « hier », « il y a 3 jours »
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const libelleDe = (t) => {
    const base = LIBELLES_SOURCE[t.source] || 'Gain d\'XP';
    if (t.source === 'palier_serie' && t.metadata?.palier) return `${base} · ${t.metadata.palier} jours`;
    return t.matiere ? `${base} · ${t.matiere}` : base;
};

function XpHistorique({ limite = 6 }) {
    const { profil, isEleve } = useXp();
    const { data, isLoading } = useTransactionsXp(profil?.xp_total, isEleve);

    const transactions = useMemo(() => {
        const liste = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
        return liste.map(normalize).filter(t => t.xp > 0).slice(0, limite);
    }, [data, limite]);

    if (!isEleve) return null;

    return (
        <div className="mt-4 rounded-2xl p-4" style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(201,223,240,.16)' }}>
            <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">📜</span>
                <h2 className="text-white font-bold" style={{ fontFamily: 'Fredoka', fontSize: 16 }}>Mes derniers gains</h2>
            </div>
            {isLoading ? (
                <p className="text-xs font-semibold py-3 text-center" style={{ color: 'rgba(255,255,255,.5)' }}>Chargement…</p>
            ) : transactions.length === 0 ? (
                <p className="text-xs font-semibold py-3 text-center" style={{ color: 'rgba(255,255,255,.5)' }}>
                    Termine un quiz pour gagner tes premiers XP !
                </p>
            ) : (
                <ul className="space-y-1.5">
                    {transactions.map(t => (
                        <li key={t.id} className="flex items-center gap-2.5 text-sm">
                            <span
                                className="flex-shrink-0 text-[11px] font-extrabold px-2 py-0.5 rounded-full tabular-nums"
                                style={{ background: 'rgba(41,182,232,.18)', color: '#8FD6F5', border: '1px solid rgba(41,182,232,.35)' }}
                            >
                                +{t.xp} XP
                            </span>
                            <span className="min-w-0 flex-1 truncate font-semibold" style={{ color: 'rgba(255,255,255,.85)' }}>
                                {libelleDe(t)}
                            </span>
                            <span className="flex-shrink-0 text-[11px] font-semibold" style={{ color: 'rgba(255,255,255,.45)' }}>
                                {dateRelative(t.date)}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default XpHistorique;
